import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import '../styles/tokens.scss';
import './styles.scss';

export interface IUptimeDrawerProps {
    title: ReactNode;
    subtitle?: ReactNode;
    footer?: ReactNode;
    children: ReactNode;
    onClose: () => void;
    width?: number | string;
    closeLabel?: string;
    closeOnScrimClick?: boolean;
    className?: string;
    escapeBlockers?: string;
}

const DEFAULT_ESCAPE_BLOCKERS = '.modal.show, .modal.in';

export const UptimeDrawer = ({
    title,
    subtitle,
    footer,
    children,
    onClose,
    width = 440,
    closeLabel,
    closeOnScrimClick = true,
    className,
    escapeBlockers = DEFAULT_ESCAPE_BLOCKERS,
}: IUptimeDrawerProps) => {
    const intl = useIntl();
    const panelRef = useRef<HTMLElement>(null);
    const onCloseRef = useRef(onClose);
    const escapeBlockersRef = useRef(escapeBlockers);
    onCloseRef.current = onClose;
    escapeBlockersRef.current = escapeBlockers;

    useEffect(() => {
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !document.querySelector(escapeBlockersRef.current)) {
                onCloseRef.current();
            }
        };
        document.addEventListener('keydown', handleKey);
        panelRef.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
        return () => document.removeEventListener('keydown', handleKey);
    }, []);

    return createPortal(
        <>
            <div className='upt-drawer__scrim' onClick={closeOnScrimClick ? onClose : undefined} />
            <aside
                ref={panelRef}
                className={`upt-drawer${className ? ` ${className}` : ''}`}
                style={{ width: typeof width === 'number' ? `min(${width}px, 100%)` : width }}
            >
                <header className='upt-drawer__header'>
                    <div className='upt-drawer__titles'>
                        <h2 className='upt-drawer__title'>{title}</h2>
                        {subtitle && <div className='upt-drawer__subtitle'>{subtitle}</div>}
                    </div>
                    <button
                        type='button'
                        className='upt-drawer__close'
                        onClick={onClose}
                        aria-label={closeLabel ?? intl.formatMessage({ id: 'intl-msg:shared.drawer.close' })}
                    >
                        ×
                    </button>
                </header>
                <div className='upt-drawer__body'>{children}</div>
                {footer && <footer className='upt-drawer__footer'>{footer}</footer>}
            </aside>
        </>,
        document.body
    );
};

export default UptimeDrawer;
